import React, { useEffect, useState } from "react";
import { BankCardsModal } from "../../../../../components/Modals/BankCardsModal";
import { ISelectCard } from "../../../../../components/Modals/BankCardsModal/BankCardsModal";
import { useLoading } from "../../../../../hooks/useLoading/useLoading";
import { IOffer } from "../../../../../models/ordersModel";
import { TranslationObject } from "../../../../../models/translationModal";
import {
    useAcceptOfferFromCustomerMutation,
    useDeclineOfferFromCustomerMutation,
    useMakeOfferOrderMutation,
} from "../../../../../store/chat/chat.api";
import { useNotification } from "../../../../../hooks/useNotification";

interface SpecialistButtonsProps {
    orderId?: string;
    price?: number;
    entities?: TranslationObject;
    offer?: IOffer | null;
}

export const SpecialistButtons: React.FC<SpecialistButtonsProps> = ({
    orderId = "",
    price,
    entities,
    offer,
}) => {
    const [visibleBankCardsModal, setVisibleBankCardsModal] = useState(false);
    const [modalContext, setModalContext] = useState<"offer" | "accept">(
        "offer"
    );
    const [typeModalProps, setTypeModalProps] = useState("cards");

    const [
        makeOfferOrder,
        { isSuccess: isOfferSuccess, isError: isOfferError },
    ] = useMakeOfferOrderMutation();
    const [declineOfferFromCustomer] = useDeclineOfferFromCustomerMutation();
    const [
        acceptOfferFromCustomer,
        { isSuccess: isAcceptSuccess, isError: isAcceptError },
    ] = useAcceptOfferFromCustomerMutation();

    const { setLoading } = useLoading();

    const { setNotification } = useNotification();
    const [isInsufficientFundsException, setInsufficientFundsException] = useState(false);

    // Обработка состояний операций
    useEffect(() => {
        if (isOfferSuccess || isAcceptSuccess) {
            setTypeModalProps("isSuccessPaymentMethod");
        }
        if (isOfferError || isAcceptError) {
            setTypeModalProps("isError");
        }
    }, [isOfferSuccess, isAcceptSuccess, isOfferError, isAcceptError]);

    const handleCardConfirm = async (selectCard: ISelectCard) => {
        if (!selectCard?.value || !orderId) return;

        setLoading(true);
        try {
            if (modalContext === "offer") {
                await makeOfferOrder({
                    orderId,
                    body: { card_id: selectCard.value },
                }).unwrap(
                ).catch((err) => {
                    if (err?.data?.payload?.exception?.includes("InsufficientFundsException")) {
                        setInsufficientFundsException(true);
                        setNotification({
                            type: "warn",
                            title: err?.data?.payload?.title ?? "",
                            description:
                                err?.data?.payload?.message ??
                                err?.data?.description ??
                                "",
                        })
                    }
                });
            } else {
                await acceptOfferFromCustomer({
                    offer_id: offer?.id ?? 0,
                    card_id: selectCard.value,
                }).unwrap(
                ).catch((err) => {
                    if (err?.data?.payload?.exception?.includes("InsufficientFundsException")) {
                        setInsufficientFundsException(true);
                        setNotification({
                            type: "warn",
                            title: err?.data?.payload?.title ?? "",
                            description:
                                err?.data?.payload?.message ??
                                err?.data?.description ??
                                "",
                        })
                    }
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDeclineOffer = async () => {
        if (offer?.id) {
            try {
                await declineOfferFromCustomer({ offer_id: offer.id }).unwrap();
            } catch {
                // Обработка ошибки
            }
        }
    };

    const closeModal = () => {
        setVisibleBankCardsModal(false);
        setTypeModalProps("cards");
    };

    // Управление прокруткой страницы
    useEffect(() => {
        document.body.style.overflow = visibleBankCardsModal
            ? "hidden"
            : "auto";
        document.documentElement.style.overflow = visibleBankCardsModal
            ? "hidden"
            : "auto";

        return () => {
            document.body.style.overflow = "auto";
            document.documentElement.style.overflow = "auto";
        };
    }, [visibleBankCardsModal]);

    const showOfferButton =
        !offer || (offer?.proposed_by === null && offer.status !== "decline") || Object.keys(offer).length === 0;

    const showAcceptRejectButtons =
        (offer &&
            offer?.proposed_by === "customer" &&
            !["specialist_accept", "specialist_decline"].includes(
                offer.status ?? ""
            )) ||
        (offer &&
            offer?.proposed_by === null &&
            !["specialist_accept", "specialist_decline", "decline"].includes(
                offer.status ?? ""
            ));

    return (
        <>
            {!isInsufficientFundsException && (<BankCardsModal
                visible={visibleBankCardsModal}
                closeModal={closeModal}
                confirmModal={handleCardConfirm}
                price={price}
                entities={entities}
                typeModalProps={typeModalProps}
                successPaymentText={{
                    title: null,
                    description: null,
                    buttonText: null,
                }}
                paymentText={{
                    description: offer && offer?.proposed_by === "customer"
                        ? entities?.charge_commission_after_customer_confirms_transaction_by_customer?.value?.replace(
                            ":price",
                            price?.toString() ?? ""
                            ) ?? null
                        : entities?.charge_commission_after_customer_confirms_transaction?.value?.replace(
                            ":price",
                            price?.toString() ?? ""
                            ) ?? null
                }}
            />)}

            <div className="form__row chat__controls">
                {showOfferButton && (
                    <div className="order-action-panel-offer">
                        <p className="order-action-panel-offer__text">
                            {entities?.specialist_chat_agreement_confirmation?.value
                                ? entities?.specialist_chat_agreement_confirmation?.value
                                : "Click when you have agreed to the terms of the order."}
                        </p>
                        <div className="popover popover--grey">
                            <button
                                className="button button--specialist-offer"
                                type="button"
                                onClick={() => {
                                    setModalContext("offer");
                                    setVisibleBankCardsModal(true);
                                }}
                            >
                                {entities?.order_offer_a_deal?.value}
                            </button>
                            <span className="popover__tip">
                                {
                                    entities?.order_offer_disclosure_request
                                        ?.value
                                }
                            </span>
                        </div>
                        <span className="popover__tip">
                            {entities?.order_offer_disclosure_request?.value}
                        </span>
                    </div>
                )}

                {showAcceptRejectButtons && (
                    <div className="order-action-panel-agreement-disagreement">
                        <button
                            className="button button--specialist-disagreement"
                            type="button"
                            onClick={handleDeclineOffer}
                        >
                            {entities?.specialist_decline_the_deal?.value
                                ? entities?.specialist_decline_the_deal?.value
                                : ""}
                        </button>
                        <button
                            className="button button--specialist-agreement"
                            type="button"
                            onClick={() => {
                                setVisibleBankCardsModal(true);
                                setModalContext("accept");
                                setTypeModalProps("cards");
                                setInsufficientFundsException(false);
                            }}
                        >
                            {entities?.specialist_accept_the_deal?.value
                                ? entities?.specialist_accept_the_deal?.value
                                : ""}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};
